import {
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useImmerReducer } from 'use-immer'
import { MdAdd, MdArrowBack } from 'react-icons/md'
import Programa from '../../../components/Programa/Programa'
import Edicao from './Edicao'
import PrimeTable from '../../../components/PrimeTable'
import { currencyMask } from '../../../utils/masks'
import dayjs from 'dayjs'
import { numbersOnly } from '../../../utils/validacoes'
import { setError, setSuccess } from '../../../components/alerts'
import { metodoPrograma } from '../../../components/api'
import { GlobalContext } from '../../../App'
import { LuCheck, LuCopy, LuLink } from 'react-icons/lu'

const mapArrayToProposta = (u = []) => {
  const safeDayjs = (val) => {
    if (!val) return null
    const d = dayjs(val)
    return d.isValid() ? d : null
  }

  return {
    idAtendimento: u?.[0] || null,
    acFormaPagamento: u?.[1],
    acQuarto: u?.[2] || null,
    cdCpf: u?.[3]?.cdCpf || '',
    dsNome: u?.[3]?.nmPaciente || '',
    dtNascimento: safeDayjs(u?.[3]?.dtNascimento),
    dsEmail: u?.[3]?.dsEmail || '',
    nrTelefone: u?.[3]?.nrTelefone || '',
    dtCheckin: safeDayjs(u?.[4]),
    dtCheckout: safeDayjs(u?.[5]),
    dsObservacoes: u?.[6] || '',
    vlSubTotal: u?.[7] || 0,
    vlCaucao: u?.[8] || 0,
    vlTotal: u?.[9] || 0,
    dtPagamentoCaucao: safeDayjs(u?.[12]),
    dsLogradouro: u?.[13] || '',
    nrLogradouro: u?.[14] || '',
    dsBairro: u?.[15] || '',
    dsComplemento: u?.[16] || '',
    cdCep: u?.[17] || '',
    acEstado: u?.[18] || null,
    acCidade: u?.[19] || null,
    acPais: u?.[25] || [['BR'], 'Brasil'],
    tpSituacao: u?.[20] || '0',
    acForma: u?.[21] || null,
    listPacotes: [],
    listTerapias: [],
    listPacientes: [],
    dsSaidaAeroporto: u?.[22] || null,
    dsSaidaH: u?.[23] || null,
    dsEndereco: u?.[24] || null,
    vlDescontoCheckin: u?.[26] || 0,
    snEstrangeiro: u?.[3]?.snEstrangeiro === 'S',
    cdDocumentoEstrangeiro: u?.[3]?.cdDocEstrangeiro ?? '',
  }
}

const initialValues = {
  tela: 'lista',
  dados: null,
  tab: 0,
  dadosEdicao: mapArrayToProposta(null),
  dadosHeader: mapArrayToProposta(null),
  dispatch: () => null,
  atualizaTotais: () => null,
  atualizaDadosCabecalho: () => null,
  handleSalvar: () => null,
  formatCPF: () => null,
}
export const PropostaContext = createContext(initialValues)
function reducer(state, action) {
  const { type, field, value } = action

  switch (type) {
    case 'setTela':
      return { ...state, tela: value }

    case 'setDados': {
      const mapped = value
        ? mapArrayToProposta(value)
        : mapArrayToProposta(null)
      return {
        ...state,
        dados: value,
        dadosEdicao: mapped,
        dadosHeader: mapped,
        tab: 0,
      }
    }

    case 'setTab':
      return { ...state, tab: value }

    case 'updateTotais': {
      const vlSubTotal = (value?.vlSubTotal ?? action.vlSubTotal) || 0
      const vlTotal = (value?.vlTotal ?? action.vlTotal) || 0
      return {
        ...state,
        dadosEdicao: {
          ...state.dadosEdicao,
          vlSubTotal,
          vlTotal,
        },
      }
    }

    case 'updateCaucao': {
      const vlCaucao = Number(value || action.vlCaucao || 0)
      const vlSubTotal = Number(state.dadosEdicao?.vlSubTotal || 0)
      const vlTotal = vlSubTotal - vlCaucao

      return {
        ...state,
        dadosEdicao: {
          ...state.dadosEdicao,
          ...(action?.valuesOld || {}),
          vlCaucao,
          vlTotal,
        },
      }
    }

    case 'setIdAtendimento':
      return {
        ...state,
        dadosEdicao: {
          ...state.dadosEdicao,
          idAtendimento: value || action.idAtendimento,
        },
        dadosHeader: {
          ...state.dadosHeader,
          idAtendimento: value || action.idAtendimento,
        },
      }

    case 'updateHeader': {
      const vals = value || action.values
      const processed = { ...vals }
      if (
        processed.dtNascimento &&
        typeof processed.dtNascimento === 'string'
      ) {
        processed.dtNascimento = dayjs(processed.dtNascimento)
      }
      if (processed.dtCheckin && typeof processed.dtCheckin === 'string') {
        processed.dtCheckin = dayjs(processed.dtCheckin)
      }
      if (processed.dtCheckout && typeof processed.dtCheckout === 'string') {
        processed.dtCheckout = dayjs(processed.dtCheckout)
      }
      if (
        processed.dtPagamentoCaucao &&
        typeof processed.dtPagamentoCaucao === 'string'
      ) {
        processed.dtPagamentoCaucao = dayjs(processed.dtPagamentoCaucao)
      }
      return { ...state, dadosHeader: processed }
    }

    case 'dadosEdicao':
      if (field) {
        const updatedValue =
          field.startsWith('dt') && typeof value === 'string'
            ? dayjs(value)
            : value

        return {
          ...state,
          dadosEdicao: { ...state.dadosEdicao, [field]: updatedValue },
        }
      }
      return { ...state, dadosEdicao: value }

    case 'RESET':
      return {
        ...state,
        dadosEdicao: mapArrayToProposta(null),
        dadosHeader: mapArrayToProposta(null),
      }

    default:
      return !!field && !!type && !!value
        ? { ...state, [type]: { ...state?.[type], [field]: value } }
        : type && !!value
          ? { ...state, [type]: value }
          : { ...state }
  }
}
export const listaSituacao = [
  ['0', 'Negociação Iniciada'],
  ['1', 'Negociação Finalizada'],
  ['2', 'Negociação Cancelada'],
  ['3', 'Em atendimento'],
  ['9', 'Atendimento finalizado'],
]

export const STATUS_MAP = {
  0: { label: 'Negociação Iniciada', color: '#1976d2', background: '#e3f2fd' },
  1: {
    label: 'Negociação Finalizada',
    color: '#ff9800',
    background: '#fff5e6',
  },
  2: { label: 'Negociação Cancelada', color: '#a7a7a7', background: '#f9f9f9' },
  3: { label: 'Em atendimento', color: '#8551ff', background: '#ffebee' },
  9: {
    label: 'Atendimento finalizado',
    color: '#02a50a',
    background: '#e2ffe1',
  },
}

export function StatusChip({ status }) {
  const info = STATUS_MAP[status]

  if (!info) return status

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        width: '100%',
        color: info.color,
        backgroundColor: info.background,
        textAlign: 'center',
      }}
    >
      {info.label}
    </span>
  )
}

export const handleVlTotal = (value, sub, descontoCheckin) => {
  const valueTotal = (value || 0) - (sub || 0) - (descontoCheckin || 0)

  return `R$ ${currencyMask(valueTotal)}`
}

const Proposta = () => {
  const { localAtendimento } = useContext(GlobalContext)
  const [state, dispatch] = useImmerReducer(reducer, initialValues)
  const [ferramentas, setFerramentas] = useState()
  const LINK_CONSENTIMENTO =
    'https://www.spatourlife.app.br/publico/termo_consentimento_publico'

  const [copiado, setCopiado] = useState(false)

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(LINK_CONSENTIMENTO)

      setCopiado(true)
      setTimeout(() => {
        setCopiado(false)
      }, 2000)
    } catch (e) {
      console.error(e)
    }
  }

  const salvaDadosAtendimento = useCallback(
    async (values) => {
      const payload = {
        ...values,
        idLocalAtendimento: localAtendimento?.[0],
        cdCpf: values?.cdCpf ? numbersOnly(values?.cdCpf) : null,
        nrTelefone: values?.nrTelefone ? numbersOnly(values?.nrTelefone) : null,
        dtNascimento: values?.dtNascimento
          ? dayjs(values.dtNascimento).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        dtCheckin: values?.dtCheckin
          ? dayjs(values.dtCheckin).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        dtCheckout: values?.dtCheckout
          ? dayjs(values.dtCheckout).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        dtPagamentoCaucao: values?.dtPagamentoCaucao
          ? dayjs(values.dtPagamentoCaucao).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        idFormaPagamento: values.acFormaPagamento?.[0]?.[0] || null,
        idQuarto: values.acQuarto?.[0]?.[0] || null,
        idCidade: values.acCidade?.[0]?.[0] || null,
        ufCidade: values.acEstado?.[0]?.[0] || null,
        cdPais: values.acPais?.[0]?.[0] || 'BR',
        idForma: values.acForma?.[0]?.[0] || null,
      }

      const res = await metodoPrograma('salva', payload)

      if (res.status) {
        dispatch({ type: 'setIdAtendimento', idAtendimento: res.idAtendimento })

        setSuccess('Salvo com Sucesso!')
      } else {
        setError(res.dsErro)
      }

      return res
    },
    [dispatch, localAtendimento],
  )

  const atualizaTotais = useCallback(
    async (idAtendimento) => {
      if (!idAtendimento) return

      const resItens = await metodoPrograma('listaTipoAtendimento', {
        idAtendimento,
      })
      const totalItens =
        resItens?.lista?.reduce(
          (acc, item) => acc + (Number(item?.[4]) || 0),
          0,
        ) || 0

      const resTerapias = await metodoPrograma('listaTerapia', {
        idAtendimento,
      })
      const totalTerapias =
        resTerapias?.lista?.reduce(
          (acc, item) => acc + (Number(item?.[4]) || 0),
          0,
        ) || 0

      const novoSubTotal = (totalItens || 0) + (totalTerapias || 0)
      const caucao = Number(state.dadosEdicao?.vlCaucao || 0)

      dispatch({
        type: 'updateTotais',
        vlSubTotal: novoSubTotal,
        vlTotal: novoSubTotal - caucao,
      })

      return {
        vlSubTotal: novoSubTotal,
        vlTotal: novoSubTotal - caucao,
        vlCaucao: caucao,
      }
    },
    [state.dadosEdicao?.vlCaucao, dispatch],
  )

  const atualizaDadosCabecalho = useCallback(
    (values) => {
      if (!values) return
      dispatch({
        type: 'updateHeader',
        values: {
          ...values,
          vlSubTotal: values.vlSubTotal || 0,
          vlCaucao: values.vlCaucao || 0,
          vlTotal: values.vlTotal || 0,
        },
      })
    },
    [dispatch],
  )

  const handleSalvar = useCallback(
    async (values) => {
      if (values?.snEstrangeiro === true) {
        values.snEstrangeiro = 'S'
      } else if (values?.snEstrangeiro === false) {
        values.snEstrangeiro = 'N'
      }
      const res = await salvaDadosAtendimento(values)

      if (res.status) {
        await atualizaTotais(
          res.idAtendimento || state.dadosEdicao.idAtendimento,
        )
      }
      return res
    },
    [atualizaTotais, salvaDadosAtendimento, state.dadosEdicao.idAtendimento],
  )

  function formatCPF(value) {
    const cleanedValue = value.replace(/\D/g, '')
    const formattedValue = cleanedValue.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      '$1.$2.$3-$4',
    )

    return formattedValue
  }

  const providerValue = useMemo(
    () => ({
      ...state,
      dispatch,
      atualizaTotais,
      atualizaDadosCabecalho,
      handleSalvar,
      formatCPF,
    }),
    [state, dispatch, atualizaTotais, atualizaDadosCabecalho, handleSalvar],
  )

  const voltar = () => {
    dispatch({ type: 'setTela', value: 'lista' })
    dispatch({ type: 'setDados', value: null })
  }

  const handleClick = (value) => {
    dispatch({ type: 'setDados', value })
    dispatch({ type: 'setTela', value: 'opcoes' })
  }

  useEffect(() => {
    switch (state.tela) {
      case 'lista':
        setFerramentas(
          <Tooltip title="Adicionar">
            <span>
              <IconButton
                onClick={() => dispatch({ type: 'setTela', value: 'opcoes' })}
              >
                <MdAdd />
              </IconButton>
            </span>
          </Tooltip>,
        )
        break
      case 'opcoes':
        setFerramentas(
          <Tooltip title="Voltar">
            <span>
              <IconButton onClick={voltar}>
                <MdArrowBack />
              </IconButton>
            </span>
          </Tooltip>,
        )
        break
      default:
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tela])

  return (
    <PropostaContext.Provider value={providerValue}>
      <Programa codigo="CtrlProposta" ferramentas={ferramentas}>
        {state.tela === 'lista' ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <PrimeTable
                codigo="CtrlProposta"
                action="lista"
                habSearch
                collumnsDateHour={[2]}
                collumnsDate={[3, 4]}
                collumnsCurrency={[6, 7]}
                rows={10}
                head={[
                  '#',
                  'Paciente',
                  'Inclusão',
                  'Check-in',
                  'Check-out',
                  'Status',
                  'Caução',
                  'Valor',
                ]}
                line={(item) => [
                  item?.[0],
                  item?.[3]?.nmPaciente || '',
                  item?.[10],
                  item?.[4],
                  item?.[5],
                  item?.[20] ? <StatusChip status={item[20]} /> : '',
                  item?.[8],
                  item?.[9],
                ]}
                onRowSelect={handleClick}
              />
            </Grid>
          </Grid>
        ) : (
          <Edicao />
        )}

        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            bgcolor: '#fafafa',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <LuLink size={22} color="#1976d2" />

            <Typography variant="h6">
              Compartilhe este link com o paciente
            </Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copie o link abaixo e encaminhe ao paciente por WhatsApp, e-mail ou
            outro meio de comunicação. Por meio dele, o paciente poderá realizar
            o<strong> aceite dos Termos de Consentimento</strong> e preencher os
            <strong> Questionários Clínicos</strong> antes do atendimento.
          </Typography>

          <TextField
            fullWidth
            label="Link do Termo de Consentimento"
            value={LINK_CONSENTIMENTO}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copiar link">
                    <IconButton onClick={copiarLink}>
                      {copiado ? <LuCheck size={18} /> : <LuCopy size={18} />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      </Programa>
    </PropostaContext.Provider>
  )
}

export default Proposta
